import { generateId } from '../generate-id'

/**
 * Sanitizes a filename by removing invalid characters and ensuring it is safe for:
 * - Cross-platform file systems (Windows, Mac, Linux)
 * - Cloud storage (S3, DigitalOcean Spaces, etc.)
 * - URL encoding/decoding
 *
 * Handles Unicode normalization, removes control characters, removes non-ASCII characters,
 * removes special characters that cause URL/S3 issues, trims whitespace,
 * and prevents reserved Windows device names.
 *
 * @param input - The raw filename string to sanitize
 * @returns A safe filename string suitable for cross-platform file systems and cloud storage
 *
 * @example
 * sanitizeFilename('WITH ±!@#$%^&*(+_)(*. .jpg')
 * // Returns: 'WITH.jpg'
 */
export function sanitizeFilename(input: string): string {
  // Handle null/undefined/empty input - generate unique fallback to prevent conflicts
  if (!input || typeof input !== 'string' || !input.trim()) {
    return `unknown-file_${generateId(3)}`
  }

  // Normalize Unicode characters to NFKC form for consistency
  const name = input.normalize('NFKC')

  // Extract extension before processing (to preserve it)
  const lastDotIndex = name.lastIndexOf('.')
  let extension = ''
  let baseName = name

  if (lastDotIndex > 0) {
    extension = name.slice(lastDotIndex + 1).toLowerCase()
    baseName = name.slice(0, lastDotIndex)
  }

  // Replace all problematic characters with underscores:
  // - Control characters (ASCII 0-31)
  // - Non-ASCII characters (> 126) like ±, ©, emoji, etc.
  // - Filesystem-invalid and URL-problematic characters
  baseName = Array.from(baseName)
    .map((ch) => {
      const code = ch.charCodeAt(0)
      // Replace control characters (ASCII 0-31) with underscore
      if (code >= 0 && code <= 31) return '_'
      // Replace non-ASCII characters (> 126) with underscore
      if (code > 126) return '_'
      // Replace filesystem-invalid and URL-problematic characters with underscore
      // Includes: < > : " / \ | ? * ! @ # $ % ^ & ( ) + = { } ; ' , ` [ ]
      if ('<>:"/\\|?*!@#$%^&()+={};\',`[]'.includes(ch)) return '_'
      return ch
    })
    .join('')

  // Normalize whitespace (replace spaces with underscores for URL safety) and trim
  baseName = baseName.replace(/\s+/g, '_').trim()

  // Normalize consecutive underscores to single
  baseName = baseName.replace(/_+/g, '_')

  // Normalize consecutive dots
  baseName = baseName.replace(/\.+/g, '.')

  // Remove leading and trailing dots/underscores
  baseName = baseName.replace(/^[._]+|[._]+$/g, '')

  // Fallback if name becomes empty - generate unique fallback to prevent conflicts
  if (!baseName) baseName = `unknown-file_${generateId(3)}`

  // Prevent Windows reserved device names
  if (/^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(baseName)) baseName = `${baseName}-file`

  // Limit length to prevent filesystem issues (leaving room for extension)
  if (baseName.length > 180) {
    baseName = baseName.slice(0, 180)
    baseName = baseName.replace(/[._]+$/, '') // Clean trailing special chars after truncation
  }

  // Sanitize extension (only alphanumeric)
  extension = extension.replace(/[^a-zA-Z0-9]/g, '')

  // Reconstruct filename
  if (extension) {
    return `${baseName}.${extension}`
  }

  return baseName
}
